import * as React from 'react'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'

import {
  Typography,
  withStyles,
  Theme,
  createStyles,
  CircularProgress,
  Grid
} from '@material-ui/core'
import { useCallback, useEffect, useRef, useState } from 'react'

import { debounce } from 'debounce'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../OneplaceLibraryProvider'
import { SelectOptions } from './types'

const styles = (_theme: Theme) => createStyles({})

const PAGE_SIZE = 100

interface IHazardAutoCompleteProps<TOption, TValue>
  extends IOneplaceLibraryContextProp {
  handleHazardChange: (
    // eslint-disable-next-line @typescript-eslint/ban-types
    event: React.ChangeEvent<{}>,
    newValue: TOption | undefined
  ) => void
  initialSelection?: TOption | null
  selectionLists?: SelectOptions | []
  getLabel: (option: TOption) => string
  getValue: (option: TOption) => TValue
  isEqual?: (a: TOption, b: TOption) => boolean
  mapResponse?: (hazard: any) => TOption
  mapOfflineItem?: (item: any) => TOption
  isOffline: boolean
  disabled?: boolean
  noLabel?: boolean
  fullWidth?: boolean
}

function HazardAutoCompleteInner<TOption, TValue>(
  props: IHazardAutoCompleteProps<TOption, TValue>
) {
  const { getLabel, getValue, isEqual, mapResponse, mapOfflineItem } = props
  const t = props.ctx.i18next.t

  const inputRef = useRef<HTMLInputElement>(null)

  const [hazards, setHazards] = useState<TOption[]>([])
  const [selectedHazard, setSelectedHazard] = useState<TOption | undefined>(
    props.initialSelection || undefined
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [page, setPage] = useState<number>(0)
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [inputValue, setInputValue] = useState(
    props.initialSelection ? getLabel(props.initialSelection) : ''
  )
  const [open, setOpen] = useState(false)

  const compareOptions = (a: TOption, b: TOption): boolean => {
    if (isEqual) return isEqual(a, b)
    return getValue(a) === getValue(b)
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  const fetchData = async (name: string, from: number) => {
    try {
      setLoading(true)
      if (props.isOffline) {
        const hazardsList = props.selectionLists || []
        const mapped: TOption[] = Array.from(hazardsList).map((item: any) =>
          mapOfflineItem ? mapOfflineItem(item) : (item as TOption)
        )
        setHazards(mapped)
        setHasMore(false)
      } else {
        const caps = await props.ctx.client.getCapabilities()
        const response = await props.ctx.client.searchHazardByName(
          name,
          from,
          PAGE_SIZE,
          caps.franchiseId
        )
        const mapped: TOption[] = response.map((hazard: any) =>
          mapResponse ? mapResponse(hazard) : (hazard as TOption)
        )
        setHazards((prev) => (from === 0 ? mapped : [...prev, ...mapped]))
        setHasMore(response.length === PAGE_SIZE)
      }
    } catch (error) {
      console.error('Error loading Hazards:', error)
      setHazards([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const name = inputValue
    void fetchData(name, page * PAGE_SIZE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, page])

  // eslint-disable-next-line react-hooks/exhaustive-deps, @typescript-eslint/no-unsafe-assignment
  const fetchDataDebounced = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    debounce((name: string, from: number) => fetchData(name, from), 1000),
    []
  )

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue)
    setPage(0)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    fetchDataDebounced(newValue, 0)
  }

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const target = e.target as HTMLElement
    const bottom =
      target.scrollHeight - target.scrollTop === target.clientHeight
    if (bottom && !loading && hasMore) {
      setPage((prevPage) => prevPage + 1)
    }
  }

  const handleChange = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    event: React.ChangeEvent<{}>,
    newValue: TOption | TOption[] | null
  ) => {
    const single = Array.isArray(newValue) ? newValue[0] ?? null : newValue
    const valueToSend = single || undefined
    setSelectedHazard(valueToSend)
    props.handleHazardChange(event, valueToSend)
    setInputValue(single ? getLabel(single) : '')
  }

  // Effect to update inputValue and selectedHazard when initialSelection changes
  useEffect(() => {
    setSelectedHazard(props.initialSelection || undefined)
    setInputValue(
      props.initialSelection ? getLabel(props.initialSelection) : ''
    )
  }, [props.initialSelection])

  return (
    <Grid item xs={12} sm={props?.fullWidth ? 12 : 6}>
      <Autocomplete
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        fullWidth
        id='hazard-search'
        noOptionsText={`No ${t(
          'customLabel_hazards'
        )} found matching your search`}
        loading={loading}
        options={hazards}
        value={selectedHazard ?? null}
        onChange={handleChange}
        getOptionSelected={(option, value) => compareOptions(option, value)}
        getOptionLabel={(option) => getLabel(option) || ''}
        renderOption={(option) => <Typography>{getLabel(option)}</Typography>}
        renderInput={(params) => (
          <TextField
            {...params}
            onBlur={() => setOpen(false)}
            variant='standard'
            label={
              props?.noLabel ? undefined : t('customLabel_hazard') + ' Involved'
            }
            value={inputValue}
            InputLabelProps={props?.noLabel ? undefined : { shrink: true }}
            onChange={(e) => handleInputChange(e.target.value)}
            inputRef={inputRef}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color='primary' size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        ListboxProps={{
          onScroll: handleScroll
        }}
        disabled={props.disabled}
      />
    </Grid>
  )
}
// Wrap with HOCs — type assertion needed since HOCs don't preserve generics
export const HazardAutoComplete = withStyles(styles)(
  withOneplaceLibraryContext(HazardAutoCompleteInner as any)
) as any as <TOption, TValue>(
  props: Omit<IHazardAutoCompleteProps<TOption, TValue>, 'ctx'>
  // eslint-disable-next-line prettier/prettier
) => React.ReactElement
